import {
  BaseField,
  type DefaultMarketAccounts,
  type FieldData,
  FieldValidationLevel,
  type FieldValidationResult,
  FieldValidationType,
  type RequiredProperties,
  type Security,
  type UnifiedLiquidityResponse,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import type { LegFormParams, LegParams } from '../../../providers/OMSContext.types';
import { getOneAccountPerMarket } from '../../OMS/utils';
import { AvailabilityEnum, type MarketSelectorItem } from '../types';
import { organizeMarketSelectorItemsByAvailability } from './OrderMarketsField';

export interface LegParamsFieldData extends FieldData<LegFormParams[]> {}

export interface InitLegSettings {
  defaultToEmpty?: boolean;
  defaultMarketAccounts?: DefaultMarketAccounts;
  allowDisabledSelections?: boolean;
}

/**
 * The LegParamsField class helps you build LegParams objects to be passed to the backend when placing multileg orders.
 * The class can either be used for building standard child legs, or be used to build the single UL leg. Depending on which
 * you are doing, you either create an instance of the class with .Create or .CreateUL.
 *
 * Internally, the class holds LegFormParams, which is an extension of the backend LegParams concept. LegFormParams holds a few additional
 * properties which helps in the building process. When you want to send the LegParams off to the backend, you invoke .getLegParamsForOrderSubmission().
 */
export class LegParamsField extends BaseField<LegParamsFieldData, LegFormParams[]> {
  constructor(initial?: Partial<LegParamsFieldData>) {
    super({
      name: 'LegParamsField',
      value: undefined,
      isRequired: true,
      isTouched: false,
      isDisabled: false,
      isVisible: true,
      placeholder: 'Select...',
      errors: [],
      isFocused: false,
      ...initial,
    });
  }

  /** Instantiate a new LegParamsField with initial (empty) leg params */
  static Create(security: RequiredProperties<Security, 'MultilegDetails'>): LegParamsField {
    return new LegParamsField({ value: getInitialLegParams(security) });
  }

  /** Instantiate a new LegParamsField with initial (empty) leg params. Since this is for UL, there will only be one array entry */
  static CreateUL(
    security: RequiredProperties<Security, 'MultilegDetails'>,
    ulResponses: Map<string, UnifiedLiquidityResponse> | undefined
  ): LegParamsField {
    return new LegParamsField({ value: getInitialULLegParams(security, ulResponses) });
  }

  get value() {
    return this.data.value;
  }

  public override setFocused(isFocused: boolean): BaseField<LegParamsFieldData, LegFormParams[]> {
    return this.updateData({ isFocused });
  }

  /** Returns whether or not every leg is initialized */
  get initialized() {
    if (this.value == null || this.value.length === 0) {
      return false; // The case where we dont even have any entries happens when we aren't instantiated (multileg is not relevant)
    }

    return this.value.every(leg => leg.initialized);
  }

  /** Returns whether or not there are any legs created. Instantiation happens before initialization. */
  private get instantiated() {
    return (this.data.value?.length ?? 0) > 0;
  }

  private getLegParamAtLegIndex(legIndex: number | undefined) {
    return this.value?.find(lp => lp.LegIndex === legIndex);
  }

  /** Returns whether or not the field is holding a UL leg. This means that the field is UL-only. */
  private get isUL() {
    const legParam = this.value?.at(0);
    // UL is modelled as LegIndex = undefined. For normal multileg legs, it is 0, 1
    return legParam != null && legParam.LegIndex === undefined;
  }

  initializeLeg({
    desiredSelections,
    desiredInitiating,
    desiredHaircutRatio,
    multilegDetails,
    items,
    settings,
    legIndex,
  }: {
    /** The selections you want the leg to be initialized with. The selections will be validated against the provided items before used. */
    desiredSelections?: string[];
    /** The initiating value you want on the leg. */
    desiredInitiating?: boolean;
    /** The haircut ratio value you want on the leg. */
    desiredHaircutRatio?: string;
    /** The Security.MultilegDetails object. Used for grabbing defaulting details for the leg. */
    multilegDetails?: Security['MultilegDetails'];
    /** The relevant MarketSelectorItems */
    items: MarketSelectorItem[];
    /** The legIndex to initialize. Undefined means UL. */
    legIndex: number | undefined;
    /** Extra behavior settings to pass. */
    settings?: InitLegSettings;
  }): LegParamsField {
    if (!this.value) {
      return this;
    }

    const initiating = desiredInitiating ?? getDefaultInitiatingForLeg(multilegDetails, legIndex);
    const haircutRatio = desiredHaircutRatio ?? getDefaultHaircutRatioForLeg(multilegDetails, legIndex);

    if (settings?.defaultToEmpty && desiredSelections == null) {
      return this.updateLegParams(
        {
          initialized: true,
          items: items,
          Markets: [],
          Initiating: initiating,
          HaircutRatio: haircutRatio,
        },
        legIndex
      );
    }

    const { availabilities, selectableMarketAccounts } = organizeMarketSelectorItemsByAvailability(items);
    if (desiredSelections != null) {
      // The user has some specific desire for selections. Think order priming, resubmission, modification. We try to set these.
      const validDesiredSelections = getValidSelections(
        desiredSelections,
        availabilities,
        // In this case, allowDisabledSelections will most likely be true
        settings?.allowDisabledSelections ?? false
      );
      return this.updateLegParams(
        {
          initialized: true,
          Markets: validDesiredSelections,
          items,
          Initiating: initiating,
          HaircutRatio: haircutRatio,
        },
        legIndex
      );
    } else {
      const defaultLegMarkets = getDefaultMarketsForLeg(multilegDetails, legIndex);
      // Defaulting case, there is no desired selection.
      let defaultedSelections: string[] | undefined = undefined;
      if (defaultLegMarkets) {
        // defaultLegMarkets are the markets specified on the security's leg details Markets array. If they are specified, use them.
        defaultedSelections = getValidSelections(
          defaultLegMarkets,
          availabilities,
          settings?.allowDisabledSelections ?? false
        );
      } else {
        // There are no default selections on the security's leg details. This means "All Markets", and so we try to select
        // one account per market, taking any defaultMarketAccounts (user's app config setting) into consideration
        defaultedSelections = getOneAccountPerMarket(selectableMarketAccounts, settings?.defaultMarketAccounts).map(
          ma => ma.Name
        );
      }

      return this.updateLegParams(
        {
          initialized: true,
          Markets: defaultedSelections,
          items: items,
          Initiating: initiating,
          HaircutRatio: haircutRatio,
        },
        legIndex
      );
    }
  }

  /** Clears the markets and market items on the leg params. Sets initialized back to false. Does not clear Initiating. */
  clearLegMarkets() {
    if (this.value == null) {
      return this; // no-op
    }

    return this.updateValue(this.value.map(legParam => ({ ...legParam, Markets: [], items: [], initialized: false })));
  }

  updateInitiatingLegs(initiating: boolean, legIndex: number | undefined): LegParamsField {
    return this.updateLegParams({ Initiating: initiating }, legIndex).setTouched(true);
  }

  updateHaircutRatio(haircutRatio: string, legIndex: number | undefined): LegParamsField {
    return this.updateLegParams({ HaircutRatio: haircutRatio }, legIndex).setTouched(true);
  }

  updateLegMarkets(markets: string[], legIndex: number | undefined): LegParamsField {
    return this.updateLegParams({ Markets: markets }, legIndex).setTouched(true);
  }

  updateLegItems(items: MarketSelectorItem[], legIndex: number | undefined): LegParamsField {
    const { availabilities } = organizeMarketSelectorItemsByAvailability(items);
    const currentSelections = this.getLegParamAtLegIndex(legIndex)?.Markets;
    if (!currentSelections) {
      // shouldnt happen, no-op
      return this;
    }

    const stillRelevantSelections = getValidSelections(currentSelections, availabilities, true);
    return this.updateLegParams({ items, Markets: stillRelevantSelections }, legIndex);
  }

  updateValue(value: LegFormParams[]): LegParamsField {
    const updated = this.updateData({ value });
    return updated.invariantCheck();
  }

  /** Apply a set of changes (delta/diff) to some leg, found by its index */
  private updateLegParams(legParamsChange: Partial<LegFormParams>, legIndex: number | undefined): LegParamsField {
    if (!this.value) {
      return this; // no-op
    }

    // We are creating both a new legParams array, and a new inner legParams object for the changed legIndex
    const newLegParams = this.value?.map(legParams => {
      return legIndex === legParams.LegIndex ? { ...legParams, ...legParamsChange } : legParams;
    });

    return this.updateValue(newLegParams);
  }

  setTouched(isTouched: boolean): LegParamsField {
    const updated = this.updateData({ isTouched });
    return updated.invariantCheck();
  }

  setIsVisible(isVisible: boolean): LegParamsField {
    const updated = this.updateData({ isVisible });
    return updated.invariantCheck();
  }

  setIsRequired(isRequired: boolean): LegParamsField {
    const updated = this.updateData({ isRequired });
    return updated.invariantCheck();
  }

  setDisabled(isDisabled: boolean): LegParamsField {
    const updated = this.updateData({ isDisabled });
    return updated.invariantCheck();
  }

  validate(): LegParamsField {
    // other fields accept rules as a parameter here. we dont have that yet, but we might...
    return this.invariantCheck();
  }

  private invariantCheck(): LegParamsField {
    if (!this.instantiated) {
      return this.updateData({ errors: [] });
    }

    const errors: FieldValidationResult[] = this.data.errors.filter(e => e.type === FieldValidationType.Rule);

    // 1. Some leg initiating rule (N/A for UL Leg)
    const someLegInitiating = this.value?.some(leg => leg.Initiating);
    if (!someLegInitiating && !this.isUL) {
      errors.push({
        message: 'At least 1 leg must initiate',
        level: FieldValidationLevel.Error,
      });
    }

    // 2. Markets present rule (also relevant for UL)
    const allLegsHaveMarkets = this.value?.every(leg => leg.Markets.length > 0);
    if (!allLegsHaveMarkets) {
      errors.push({
        message: 'Markets are required for all legs',
        level: FieldValidationLevel.Error,
      });
    }

    // 3. Haircut Ratio range rule: valid range is [0, 1) (1 is not allowed)
    const haircutRatioOutOfRange = this.value?.some(leg => {
      if (leg.HaircutRatio == null || leg.HaircutRatio === '') {
        return false;
      }
      const numValue = parseFloat(leg.HaircutRatio);
      return isNaN(numValue) || numValue < 0 || numValue >= 1;
    });
    if (haircutRatioOutOfRange) {
      errors.push({
        message: 'Haircut Ratio must be at least 0 and less than 1',
        level: FieldValidationLevel.Error,
      });
    }

    return this.updateData({ errors });
  }

  private updateData(data: Partial<LegParamsFieldData>): LegParamsField {
    return new LegParamsField({ ...this.data, ...data });
  }

  /** Maps LegFormParams -> LegParams for use when sending to the backend */
  getLegParamsForOrderSubmission(): LegParams[] {
    if (!this.value) {
      return [];
    }

    return this.value.map(lp => ({
      Markets: lp.Markets,
      Initiating: lp.Initiating,
      Symbol: lp.Symbol,
      LegIndex: lp.LegIndex,
      HaircutRatio: lp.HaircutRatio,
    }));
  }
}

function getInitialLegParams(security: RequiredProperties<Security, 'MultilegDetails'>) {
  const initiating = [
    getDefaultInitiatingForLeg(security.MultilegDetails, 0),
    getDefaultInitiatingForLeg(security.MultilegDetails, 1),
  ];
  const haircutRatios = [
    getDefaultHaircutRatioForLeg(security.MultilegDetails, 0),
    getDefaultHaircutRatioForLeg(security.MultilegDetails, 1),
  ];

  const legParams: LegFormParams[] = [
    {
      initialized: false,
      items: undefined,
      Markets: [],
      Initiating: initiating[0],
      HaircutRatio: haircutRatios[0],
      Symbol: security.MultilegDetails.Legs[0].Symbol,
      LegIndex: 0,
    },
    {
      initialized: false,
      items: undefined,
      Markets: [],
      Initiating: initiating[1],
      HaircutRatio: haircutRatios[1],
      Symbol: security.MultilegDetails.Legs[1].Symbol,
      LegIndex: 1,
    },
  ];

  return legParams;
}

function getInitialULLegParams(
  multilegSecurity: Security,
  ulResponses: Map<string, UnifiedLiquidityResponse> | undefined
): LegFormParams[] {
  const nativeSymbol = ulResponses?.get(multilegSecurity.Symbol)?.Tokens.at(0)?.Symbol;
  if (!nativeSymbol) {
    return [];
  }

  return [
    {
      Symbol: nativeSymbol,
      Markets: [],
      Initiating: false,
      HaircutRatio: '0',
      LegIndex: undefined, // LegIndex: undefined means UL Leg. This is how the backend works as well.
      initialized: false,
      items: undefined,
    },
  ];
}

function getValidSelections(
  selections: string[],
  availabilities: Map<string, AvailabilityEnum>,
  allowDisabledSelections: boolean
): string[] {
  return selections.filter(s => {
    const availability = availabilities.get(s);
    if (availability == null) {
      return false;
    }

    if (allowDisabledSelections) {
      return availability >= AvailabilityEnum.Disabled; // allow disabled: >=
    }

    return availability > AvailabilityEnum.Disabled; // do not allow disabled: >
  });
}

function getDefaultMarketsForLeg(multilegDetails: Security['MultilegDetails'], legIndex: number | undefined) {
  if (legIndex == null) {
    return undefined;
  }

  const legMarkets = multilegDetails?.Legs.at(legIndex)?.Markets;
  return legMarkets ? compact(legMarkets.map(m => m.MarketAccount)) : undefined;
}

function getDefaultInitiatingForLeg(multilegDetails: Security['MultilegDetails'], legIndex: number | undefined) {
  if (legIndex == null) {
    return false;
  }

  return multilegDetails?.Parameters.LegParams.at(legIndex)?.Initiating ?? false;
}

function getDefaultHaircutRatioForLeg(
  multilegDetails: Security['MultilegDetails'],
  legIndex: number | undefined
): string {
  if (legIndex == null) {
    return '0';
  }

  return multilegDetails?.Parameters.LegParams.at(legIndex)?.HaircutRatio ?? '0';
}
